/**
 * shared widget includes: the resolver and the frame markup.
 *
 * A port of the catalogue's reference resolver (`reference/resolve-includes.js`):
 *  - the startup set loads first in every frame; a startup id that fails is never fatal;
 *  - then the declared ids; every row's `includes` (its dependencies, or a package's members) expand in place,
 *    depth first, before the row's own CSS and JS;
 *  - every id loads once, at its first position; deeper than MAX_DEPTH below the given list is refused, so is a loop;
 *  - a row whose `include_id` is not the id asked for counts as failed;
 *  - status is informational (retired is served with a warning).
 * Markup: a diagnostics bootstrap (defines `window.aiwb` and `aiwb.includes()`), then per include its CSS in a
 * `<style>` and its JS in a `<script>`, each tagged `data-aiwb-include`. Unsafe bodies are refused, never rewritten.
 */

export const MAX_INCLUDE_DEPTH = 3;
export const INCLUDE_ID_RE = /^[a-z][a-z0-9-]{2,63}$/;
export const INCLUDE_CORE_FIELDS = ['include_id', 'kind', 'css', 'js', 'includes', 'status'] as const;

/** A `ui_includes` row in the Core row shape (only the fields the frontend uses). */
export interface IncludeRow {
  include_id: string;
  kind?: 'css' | 'js' | 'both' | 'package' | string;
  status?: string;
  css?: string;
  js?: string;
  includes?: string[];
}

/** Where a row came from: the startup set, the build's static library (declared ids), or a Core read. */
export type IncludeSource = 'startup' | 'library' | 'core';

/**
 * One declaring list, with where it was declared: `root:<kind>:<id>` (a form, dashboard or page root, outermost
 * first), `template:<id>` (a stored HTML template) or `node` (the HTML node itself). The startup set is `startup`.
 */
export interface IncludeLayer {
  origin: string;
  ids: readonly string[];
}

/** One line of `aiwb.includes()` in the frame. */
export interface IncludeDiagnostic {
  id: string;
  /** Where the row was read: the startup files or Core. */
  source: IncludeSource | null;
  /** Which list brought it in: `startup`, `root:<kind>:<id>`, `template:<id>` or `node` (dependencies inherit it). */
  origin?: string;
  status: string | null;
  kind?: string;
  order: number | null;
  error?: string;
}

export interface IncludeFailure {
  id: string;
  error: string;
  fatal: boolean;
}

export interface IncludeResolution {
  /** false when a declared id (outside the startup set) is missing, refused, in a loop or too deep. */
  ok: boolean;
  /** Rows to inject, in order (packages expanded, never injected themselves). */
  assets: IncludeRow[];
  /** Declared ids that could not be served (for the widget's error state). */
  missing: string[];
  failures: IncludeFailure[];
  warnings: string[];
  diagnostics: IncludeDiagnostic[];
}

export class IncludeResolveError extends Error {
  constructor(
    readonly code: 'include_id_invalid' | 'include_cycle' | 'include_depth_exceeded',
    message: string,
    readonly ids: string[],
  ) {
    super(message);
  }
}

/** Why a body may not be injected, or null. Same rule as the catalogue checks. */
export function unsafeBody(text: unknown, field: 'css' | 'js'): string | null {
  const t = String(text ?? '');
  if (field === 'css' && /<\/style/i.test(t)) return 'css body contains </style';
  if (field === 'js' && /<\/script/i.test(t)) return 'js body contains </script';
  if (/<!--/.test(t) && /<script/i.test(t)) return `${field} body contains <!-- together with <script`;
  return null;
}

/** Why a loaded row cannot be used for `id`, or null. */
export function rowProblem(id: string, row: unknown): string | null {
  if (!row || typeof row !== 'object') return 'not found';
  const r = row as IncludeRow;
  if (r.include_id !== id) return 'the loaded row holds a different include_id';
  if (r.includes != null && (!Array.isArray(r.includes) || !r.includes.every((x) => typeof x === 'string'))) {
    return 'includes is not a list of ids';
  }
  for (const field of ['css', 'js'] as const) {
    if (r[field] != null) {
      if (typeof r[field] !== 'string') return `${field} is not text`;
      const why = unsafeBody(r[field], field);
      if (why) return 'refused: ' + why;
    }
  }
  return null;
}

/** The include ids an HTML node declares: strings only, as given. */
export function declaredIds(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((x): x is string => typeof x === 'string') : [];
}

/** A plain id list is the node's own layer; empty layers are dropped. */
export function toLayers(given: readonly IncludeLayer[] | readonly string[]): IncludeLayer[] {
  const layers =
    given.length && typeof given[0] === 'string'
      ? [{ origin: 'node', ids: given as readonly string[] }]
      : (given as readonly IncludeLayer[]);
  return layers.filter((l) => l.ids.length > 0);
}

/** Stable key of the effective layers (ids and where each list came from). '' when nothing is declared. */
export function layersKey(given: readonly IncludeLayer[] | readonly string[]): string {
  return toLayers(given)
    .map((l) => `${l.origin}=${l.ids.join(',')}`)
    .join('|');
}

/** Every declared id, in layer order (repeats kept; the resolver keeps the first). */
export function flatIds(given: readonly IncludeLayer[] | readonly string[]): string[] {
  return toLayers(given).flatMap((l) => [...l.ids]);
}

/**
 * Resolve synchronously against rows in hand.
 * @param startup  the startup ids, in load order
 * @param given    the declared layers, outermost first (roots, then a stored template's, then the node's own); a
 *                 plain id list is the node's own
 * @param getRow   a usable row, or null (then `failures.get(id)` says why, default "not found")
 */
export function resolveIncludes(
  startup: readonly string[],
  given: readonly IncludeLayer[] | readonly string[],
  getRow: (id: string) => IncludeRow | null,
  opts: { sources?: Map<string, IncludeSource>; failures?: Map<string, string>; maxDepth?: number } = {},
): IncludeResolution {
  const maxDepth = opts.maxDepth ?? MAX_INCLUDE_DEPTH;
  const sources = opts.sources ?? new Map<string, IncludeSource>();
  const loadErrors = opts.failures ?? new Map<string, string>();
  const loaded = new Set<string>();
  /** Startup ids that failed: a declared reference tries again (library, then Core), and never fails the widget. */
  const startupFailed = new Set<string>();
  const result: IncludeResolution = { ok: true, assets: [], missing: [], failures: [], warnings: [], diagnostics: [] };

  const visit = (ids: readonly string[], depth: number, path: string[], isStartup: boolean, origin: string): void => {
    for (const id of ids) {
      if (typeof id !== 'string' || !INCLUDE_ID_RE.test(id)) {
        throw new IncludeResolveError('include_id_invalid', `Include id is not well formed: ${JSON.stringify(id)}`, [
          String(id),
        ]);
      }
      if (path.includes(id)) {
        throw new IncludeResolveError('include_cycle', `Include loop: ${[...path, id].join(' -> ')}`, [...path, id]);
      }
      if (loaded.has(id)) continue; // repeat: first position wins
      if (depth > maxDepth) {
        throw new IncludeResolveError(
          'include_depth_exceeded',
          `Include nesting deeper than ${maxDepth}: ${[...path, id].join(' -> ')}`,
          [...path, id],
        );
      }
      loaded.add(id);
      const row = getRow(id);
      if (!row || row.include_id !== id) {
        const error = row ? 'the loaded row holds a different include_id' : (loadErrors.get(id) ?? 'not found');
        const fatal = !isStartup && !startupFailed.has(id);
        result.failures.push({ id, error, fatal });
        if (isStartup) {
          result.warnings.push(`startup include ${id} unavailable (${error}); frames render without it`);
          // Not loaded: a declared reference to the same id (e.g. a package member) is resolved on its own.
          loaded.delete(id);
          startupFailed.add(id);
        } else if (fatal) result.missing.push(id);
        result.diagnostics.push({ id, source: sources.get(id) ?? null, origin, status: null, order: null, error });
        continue;
      }
      if (row.status === 'retired') result.warnings.push(`include ${id} is retired`);
      const entry: IncludeDiagnostic = {
        id,
        source: sources.get(id) ?? null,
        origin,
        status: row.status ?? null,
        kind: row.kind,
        order: null,
      };
      result.diagnostics.push(entry);
      visit(row.includes ?? [], depth + 1, [...path, id], isStartup, origin); // dependencies (or members) first
      if (row.kind !== 'package') {
        result.assets.push(row);
        entry.order = result.assets.length;
      }
    }
  };

  visit(startup, 0, [], true, 'startup');
  for (const layer of toLayers(given)) visit(layer.ids, 0, [], false, layer.origin);
  result.ok = result.missing.length === 0;
  return result;
}

/**
 * The ids reachable from `ids` (through known rows' `includes`) that are not known yet: what a resolution still has
 * to read. Bounded by the depth limit and loop-safe; the resolver reports depth and loops itself.
 */
export function unknownIds(
  ids: readonly string[],
  isKnown: (id: string) => boolean,
  getRow: (id: string) => IncludeRow | null,
): string[] {
  const unknown = new Set<string>();
  /** Shallowest depth each id was walked at: met again higher up, it is walked again (its members may be in reach). */
  const seen = new Map<string, number>();
  const walk = (list: readonly string[], depth: number): void => {
    for (const id of list) {
      // Nothing deeper than the resolver accepts is ever read: it refuses such a chain before reading the row.
      if (typeof id !== 'string' || !INCLUDE_ID_RE.test(id) || depth > MAX_INCLUDE_DEPTH) continue;
      if ((seen.get(id) ?? Infinity) <= depth) continue;
      seen.set(id, depth);
      if (!isKnown(id)) {
        unknown.add(id);
        continue;
      }
      walk(getRow(id)?.includes ?? [], depth + 1);
    }
  };
  walk(ids, 0);
  return [...unknown];
}

/** The private, frozen global holding the bootstrap's hooks (start, loaded, end). */
export const INCLUDE_HOOKS = '__aiwbIncludeHooks';

/**
 * The diagnostics bootstrap, injected before any include. Defines `window.aiwb` (add-only) with `aiwb.includes()`,
 * records which include is loading, and attributes a load-time error to the include that threw. The hooks live on a
 * separate frozen global, so an include that replaces `window.aiwb` cannot break the includes after it. The frame
 * logs nothing itself: the host logs each failure once per page.
 */
export function bootstrapScript(diagnostics: IncludeDiagnostic[]): string {
  const data = JSON.stringify(diagnostics).replace(/</g, '\\u003c');
  return [
    '(function () {',
    '  var aiwb = window.aiwb;',
    '  if (!aiwb || (typeof aiwb !== "object" && typeof aiwb !== "function")) aiwb = window.aiwb = {};',
    '  var list = ' + data + ';',
    '  var byId = {};',
    '  list.forEach(function (d) {',
    '    d.loaded = !d.error && d.kind !== "js" && d.kind !== "both";',
    '    byId[d.id] = d;',
    '  });',
    '  var current = null;',
    '  var hooks = Object.freeze({',
    '    start: function (id) { current = id; },',
    '    loaded: function (id) { if (byId[id]) byId[id].loaded = true; },',
    '    end: function (id) { if (current === id) current = null; }',
    '  });',
    `  Object.defineProperty(window, "${INCLUDE_HOOKS}",`,
    '    { value: hooks, writable: false, enumerable: false, configurable: false });',
    '  window.addEventListener("error", function (e) {',
    '    if (current && byId[current] && !byId[current].error) {',
    '      byId[current].error = String((e && e.message) || "error while loading");',
    '    }',
    '  });',
    '  if (!aiwb.includes) aiwb.includes = function () {',
    '    return list.map(function (d) { var c = {}; for (var k in d) c[k] = d[k]; return c; });',
    '  };',
    '})();',
  ].join('\n');
}

/**
 * Head markup for a frame: the bootstrap, then per include (in order) its `<style>` and then its `<script>`. Each
 * include's script is preceded by a start-marker script and followed by an end-marker script (the script itself
 * ends with the loaded hook), so a parse error or a throw is attributed to it and a later error is never blamed on
 * it. Ids were checked against the id pattern, so they are safe in an attribute and
 * a JS string; unsafe bodies were refused before this point (checked again here).
 */
export function includeMarkup(assets: IncludeRow[], diagnostics: IncludeDiagnostic[]): string {
  const out = [`<script data-aiwb-include-bootstrap>\n${bootstrapScript(diagnostics)}\n</script>`];
  for (const a of assets) {
    const id = a.include_id;
    if (!INCLUDE_ID_RE.test(id))
      throw new IncludeResolveError('include_id_invalid', 'Include id is not well formed', [id]);
    for (const field of ['css', 'js'] as const) {
      const why = a[field] ? unsafeBody(a[field], field) : null;
      if (why) throw new Error(`${id}: ${why}`);
    }
    const js = JSON.stringify(id);
    if (a.css) out.push(`<style data-aiwb-include="${id}">\n${a.css}\n</style>`);
    if (a.js) {
      // The start marker is its own script, so an include whose script does not even parse (a syntax error runs
      // none of it) is still the current include when the error event fires, and is named in aiwb.includes().
      out.push(`<script data-aiwb-include-start="${id}">${INCLUDE_HOOKS}.start(${js});</script>`);
      const loaded = `${INCLUDE_HOOKS}.loaded(${js});`;
      out.push(`<script data-aiwb-include="${id}">\n${a.js}\n;${loaded}\n</script>`);
      out.push(`<script data-aiwb-include-end="${id}">${INCLUDE_HOOKS}.end(${js});</script>`);
    }
  }
  return out.join('\n');
}

/**
 * Put `markup` into a document by SLICING, never as a `String.replace` replacement string (which would expand
 * `$&`, `$'`, `` $` `` and `$$` inside an include body).
 */
export function insertAt(doc: string, index: number, markup: string): string {
  return doc.slice(0, index) + markup + doc.slice(index);
}
